import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is stored in localStorage
    const storedUser = localStorage.getItem('spelling_bee_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        // Error parsing stored user
        localStorage.removeItem('spelling_bee_user');
      }
    }
    setLoading(false);
  }, []);

  const signIn = async (userData) => {
    try {
      setLoading(true);
      
      // Try to create user in the database (will succeed if new, fail silently if exists)
      let userTypeInfo = {};
      try {
        const response = await apiService.createUser(userData);
        if (response.success) {
          userTypeInfo = {
            user_type: response.user_type || 'admin',
            competitor_id: response.competitor_id || null
          };
        }
      } catch (error) {
        // Check if this is a blocked user registration error
        if (error.message && error.message.includes('New user registration is currently disabled')) {
          throw new Error('New user registration is currently disabled. Please contact the administrator if you need access.');
        }
        // For other errors (user might already exist), continue silently
      }

      // Merge user data with competitor info
      const enhancedUserData = {
        ...userData,
        ...userTypeInfo
      };

      // Store user data
      setUser(enhancedUserData);
      localStorage.setItem('spelling_bee_user', JSON.stringify(enhancedUserData));
      
    } catch (error) {
      // Error during sign in
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = () => {
    setUser(null);
    localStorage.removeItem('spelling_bee_user');
    
    // Clear any Google auth state
    if (window.google && window.google.accounts) {
      window.google.accounts.id.disableAutoSelect();
    }
  };

  const value = {
    user,
    signIn,
    signOut,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};